// noinspection JSUnusedGlobalSymbols
// This file IS the central browser-storage service; the no-direct-localstorage
// inspection exempts it by filename ("safeLocalStorage.ts"). Keep the filename.
/**
 * Central, guarded browser-storage setter — the ONE low-level writer that every
 * other service routes through (localStorage.service, redirectState,
 * sidebarStatus, latestVisitedOrganization). Direct
 * `localStorage`/`sessionStorage` writes elsewhere are banned by the
 * `inspections/no-direct-localstorage.inspection.kts` static check, which exempts
 * only this file.
 *
 * The runtime layer of the three-layer invite-link token protection (plan §5.7,
 * D11): on EVERY write it scans the stringified value for the invite-link token
 * prefix (`npinv_`). On a match it REFUSES the write — throwing in dev/test (so a
 * leak fails loud and tests catch it) and logging a scrubbed Sentry alert in prod
 * (never silently scrub-and-continue). The token must never reach persistent
 * storage; the `/invite` flow keeps it in sessionStorage held by the page only —
 * and even that goes through here, so a key/value carrying the prefix is rejected
 * regardless of which backing store is targeted.
 */
import { INVITE_LINK_TOKEN_PREFIX } from '@/constants/inviteLink'
// Lazy: a static `@/lib/logger` import would close the cycle
// safeLocalStorage → logger → appDebugService → safeLocalStorage.
import { errorViaLoggerToSentry, warnViaLogger } from '@/lib/lazyLogger'

export type BrowserStorageKind = 'local' | 'session'

/**
 * Thrown when a write is refused because its key or value contains an
 * invite-link token. Surfaced in dev/test so the leak fails loud and is
 * catchable by tests.
 */
export class InviteTokenInStorageError extends Error {
  constructor(storageKey: string) {
    super(
      `Refused to persist a value containing an invite-link token (key="${storageKey}"). ` +
        `Invite-link tokens must never be written to browser storage.`,
    )
    this.name = 'InviteTokenInStorageError'
  }
}

/**
 * True in dev/test, false in production builds. In dev/test we throw on a
 * tripped tripwire; in prod we refuse-and-log so we never crash a user session
 * over a defensive guard, while still alerting Sentry.
 */
function shouldThrowOnTokenLeak(): boolean {
  return import.meta.env.MODE !== 'production'
}

function containsInviteToken(key: string, value: string): boolean {
  return key.includes(INVITE_LINK_TOKEN_PREFIX) || value.includes(INVITE_LINK_TOKEN_PREFIX)
}

function resolveStorage(kind: BrowserStorageKind): Storage | null {
  if (typeof window === 'undefined') return null
  return kind === 'session' ? window.sessionStorage : window.localStorage
}

/**
 * Guard a key/value pair against the invite-link token tripwire. Returns true
 * when the write is allowed to proceed, false when it must be refused. Throws in
 * dev/test on a trip so the leak is loud and catchable.
 */
function assertNoInviteToken(storageKey: string, value: string): boolean {
  if (!containsInviteToken(storageKey, value)) return true

  if (shouldThrowOnTokenLeak()) {
    throw new InviteTokenInStorageError(storageKey)
  }

  // Production: refuse the write and alert Sentry with a SCRUBBED message — never
  // include the value, and identify only the key so the token itself is not logged.
  errorViaLoggerToSentry(
    `Refused to persist a value containing an invite-link token to browser storage (key="${storageKey}").`,
  )
  return false
}

export const safeLocalStorage = {
  /**
   * Write `value` under `key`. Routes through the invite-token tripwire.
   * @param key storage key
   * @param value stringified value to persist
   * @param kind which backing store (defaults to localStorage)
   */
  set(key: string, value: string, kind: BrowserStorageKind = 'local'): void {
    if (!assertNoInviteToken(key, value)) return

    const storage = resolveStorage(kind)
    if (!storage) return

    try {
      storage.setItem(key, value)
    } catch (error) {
      warnViaLogger(`Failed to write ${key} to ${kind}Storage:`, error)
    }
  },

  /**
   * The SINGLE sanctioned path for an invite-link token to enter browser
   * storage. The `/invite` landing flow parks the captured token in
   * sessionStorage so it survives the OAuth round-trip; every other write
   * (including all of `set`) refuses a `npinv_` value via the tripwire. This
   * method exists so that the one legitimate token-stash is explicit,
   * greppable, and confined to sessionStorage (per-tab, never on disk) — it is
   * deliberately NOT a general setter and never targets localStorage.
   */
  setInviteToken(key: string, token: string): void {
    const storage = resolveStorage('session')
    if (!storage) return

    try {
      storage.setItem(key, token)
    } catch (error) {
      warnViaLogger(`Failed to write ${key} to sessionStorage:`, error)
    }
  },

  get(key: string, kind: BrowserStorageKind = 'local'): string | null {
    const storage = resolveStorage(kind)
    if (!storage) return null

    try {
      return storage.getItem(key)
    } catch (error) {
      warnViaLogger(`Failed to read ${key} from ${kind}Storage:`, error)
      return null
    }
  },

  remove(key: string, kind: BrowserStorageKind = 'local'): void {
    const storage = resolveStorage(kind)
    if (!storage) return

    try {
      storage.removeItem(key)
    } catch (error) {
      warnViaLogger(`Failed to clear ${key} from ${kind}Storage:`, error)
    }
  },

  /**
   * True when `kind` storage is actually writable. Distinct from `get()`
   * returning null, which is ALSO the result for a legitimately absent key —
   * this probes writability itself (e.g. false in Safari private-mode
   * sessionStorage) via a throwaway set+remove.
   */
  isAvailable(kind: BrowserStorageKind = 'local'): boolean {
    const storage = resolveStorage(kind)
    if (!storage) return false

    const probeKey = '__nopressure_storage_probe__'
    try {
      storage.setItem(probeKey, '1')
      storage.removeItem(probeKey)
      return true
    } catch {
      return false
    }
  },
}
